import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useListSizingExperiments from "@babackapp/private-api-client/client/use-list-sizing-experiments";
import useSizingExperimentResults from "@babackapp/private-api-client/client/use-sizing-experiment-results";
import { Badge } from "@babackapp/ui/components/badge";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { DateRangePicker } from "@babackapp/ui/components/date-range-picker";
import { format, subMonths } from "date-fns";
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Loader2,
  Info,
  BarChart3,
} from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

// craftsman-ignore: TS002
export default function SizingExperimentResultsPage() {
  const { experimentId } = useParams<{ experimentId: string }>();
  const { data: shop } = useCurrentShopSuspense();
  const [dateRange, setDateRange] = useState(() => ({
    from: subMonths(Date.now(), 3).getTime(),
    to: Date.now(),
  }));

  const { data: experiments } = useListSizingExperiments(shop.id);
  const experiment = experiments?.find(
    (e) => e.experimentId === experimentId,
  );

  const { data: results, isLoading } = useSizingExperimentResults({
    shopId: shop.id,
    experimentId: experimentId ?? "",
    fromTimestamp: dateRange.from,
    toTimestamp: dateRange.to,
  });

  const shown = results?.variants?.find((v) => v.variant === "shown");
  const hidden = results?.variants?.find((v) => v.variant === "hidden");

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-[1200px] lg:px-16 lg:py-6 px-6 py-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="../experiments"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-semibold text-foreground">
                  {experiment?.name ?? "Experiment"}
                </h2>
                {experiment && (
                  <Badge
                    variant={experiment.isActive ? "default" : "secondary"}
                  >
                    {experiment.isActive ? "Active" : "Inactive"}
                  </Badge>
                )}
              </div>
              {experiment && (
                <p className="text-sm text-muted-foreground">
                  Split: {experiment.splitPercent}% shown
                  {experiment.startedAt &&
                    ` · Started ${format(new Date(experiment.startedAt), "dd/MM/yyyy")}`}
                  {experiment.endedAt &&
                    ` · Ended ${format(new Date(experiment.endedAt), "dd/MM/yyyy")}`}
                </p>
              )}
            </div>
          </div>
          <DateRangePicker
            className="w-auto"
            range={{
              from: dateRange.from,
              to: dateRange.to,
            }}
            onSelect={(range) => {
              if (range.from && range.to) {
                setDateRange({
                  from: new Date(range.from).getTime(),
                  to: new Date(range.to).getTime(),
                });
              }
            }}
            maxDate={new Date()}
            closeOnSelect
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !shown && !hidden ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center h-48 gap-2">
              <Info className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No tagged orders found for this experiment in the selected
                period.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Conversion rate (shown only) */}
            {(results?.impressionCount ?? 0) > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Conversion (shown variant)
                </h3>
                <div className="flex gap-4">
                  <MetricCard
                    label="Recommendations"
                    value={results?.impressionCount ?? 0}
                    format="number"
                  />
                  <MetricCard
                    label="Orders"
                    value={shown?.orderCount ?? 0}
                    format="number"
                  />
                  <MetricCard
                    label="Conversion Rate"
                    value={shown?.conversionRate ?? 0}
                    format="percent"
                  />
                </div>
              </div>
            )}

            {/* Variant comparison */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Variant Comparison
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <VariantColumn
                  title="Shown (widget visible)"
                  metrics={shown}
                />
                <VariantColumn
                  title="Hidden (control group)"
                  metrics={hidden}
                />
              </div>
            </div>

            {/* Return rate delta */}
            {shown && hidden && shown.orderCount > 0 && hidden.orderCount > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        Return Rate Impact
                      </p>
                      <ReturnRateDelta
                        shownRate={shown.returnRate}
                        hiddenRate={hidden.returnRate}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

type VariantMetrics = {
  variant: string;
  orderCount: number;
  orderVolume: number;
  averageOrderValue: number;
  returnedOrderCount: number;
  returnRate: number;
  conversionRate: number;
};

function VariantColumn({
  title,
  metrics,
}: {
  title: string;
  metrics: VariantMetrics | undefined;
}) {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium">{title}</h4>
      <div className="grid grid-cols-2 gap-3">
        <MetricCard
          label="Orders"
          value={metrics?.orderCount ?? 0}
          format="number"
        />
        <MetricCard
          label="Order Volume"
          value={metrics?.orderVolume ?? 0}
          format="currency"
        />
        <MetricCard
          label="Avg Order Value"
          value={metrics?.averageOrderValue ?? 0}
          format="currency"
        />
        <MetricCard
          label="Return Rate"
          value={metrics?.returnRate ?? 0}
          format="percent"
        />
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  format: fmt,
}: {
  label: string;
  value: number;
  format: "number" | "currency" | "percent";
}) {
  let formatted: string;
  if (fmt === "percent") {
    formatted = `${(value * 100).toFixed(1)}%`;
  } else if (fmt === "currency") {
    formatted = `€${value.toFixed(2)}`;
  } else {
    formatted = value.toLocaleString();
  }

  return (
    <Card className="flex-1">
      <CardContent className="pt-4 pb-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <span className="text-xl font-bold tabular-nums">{formatted}</span>
      </CardContent>
    </Card>
  );
}

function ReturnRateDelta({
  shownRate,
  hiddenRate,
}: {
  shownRate: number;
  hiddenRate: number;
}) {
  const delta = shownRate - hiddenRate;
  const isPositive = delta < 0;

  return (
    <div className="flex items-center gap-2 mt-0.5">
      <span className="text-sm text-muted-foreground">
        Shown {(shownRate * 100).toFixed(1)}% vs Hidden{" "}
        {(hiddenRate * 100).toFixed(1)}%
      </span>
      {delta !== 0 && (
        <Badge
          variant={isPositive ? "success" : "destructive"}
          className="gap-1"
        >
          {isPositive ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUp className="h-3 w-3" />
          )}
          {Math.abs(delta * 100).toFixed(1)}%
        </Badge>
      )}
    </div>
  );
}
